import { usePageRefresh } from '../../../context/PageRefreshContext';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    getComplaintCategories,
    createComplaintCategory,
    updateComplaintCategory,
    deleteComplaintCategory,
} from '../../../redux/actions/SocietyPortal/ComplaintAction/ComplaintAction';
import { getUsernamesDropdown } from '../../../redux/actions/SocietyPortal/EmployeeAction/EmployeeAction';
import { selectedSocietyStorer } from '../../../utils/webStorer';
import { SOCIETY_PORTAL, SOCIETY_PORTAL_COMPLAINTS } from '../../../Routing/routes';

// Palette matches Tailwind's semantic colors — the swatch is the source of truth
// for how the badge is tinted on the Complaints list, so keep keys stable.
const COLOR_PALETTE = [
    { key: 'rose',    bg: 'tw-bg-rose-100',    text: 'tw-text-rose-700',    ring: 'tw-ring-rose-500',    swatch: 'tw-bg-rose-500' },
    { key: 'amber',   bg: 'tw-bg-amber-100',   text: 'tw-text-amber-700',   ring: 'tw-ring-amber-500',   swatch: 'tw-bg-amber-500' },
    { key: 'emerald', bg: 'tw-bg-emerald-100', text: 'tw-text-emerald-700', ring: 'tw-ring-emerald-500', swatch: 'tw-bg-emerald-500' },
    { key: 'blue',    bg: 'tw-bg-blue-100',    text: 'tw-text-blue-700',    ring: 'tw-ring-blue-500',    swatch: 'tw-bg-blue-500' },
    { key: 'violet',  bg: 'tw-bg-violet-100',  text: 'tw-text-violet-700',  ring: 'tw-ring-violet-500',  swatch: 'tw-bg-violet-500' },
    { key: 'cyan',    bg: 'tw-bg-cyan-100',    text: 'tw-text-cyan-700',    ring: 'tw-ring-cyan-500',    swatch: 'tw-bg-cyan-500' },
    { key: 'slate',   bg: 'tw-bg-slate-200',   text: 'tw-text-slate-700',   ring: 'tw-ring-slate-500',   swatch: 'tw-bg-slate-500' },
    { key: 'fuchsia', bg: 'tw-bg-fuchsia-100', text: 'tw-text-fuchsia-700', ring: 'tw-ring-fuchsia-500', swatch: 'tw-bg-fuchsia-500' },
];

const ICON_LIBRARY = [
    { key: 'mdi:pipe-wrench',            label: 'Plumbing' },
    { key: 'mdi:lightning-bolt',         label: 'Electrical' },
    { key: 'mdi:shield-check-outline',   label: 'Security' },
    { key: 'mdi:broom',                  label: 'Housekeeping' },
    { key: 'mdi:tools',                  label: 'Maintenance' },
    { key: 'mdi:volume-vibrate',         label: 'Noise' },
    { key: 'mdi:car-outline',            label: 'Parking' },
    { key: 'mdi:elevator',               label: 'Elevator' },
    { key: 'mdi:water',                  label: 'Water' },
    { key: 'mdi:trash-can-outline',      label: 'Garbage' },
    { key: 'mdi:bug-outline',            label: 'Pest' },
    { key: 'mdi:home-city-outline',      label: 'Common Area' },
    { key: 'mdi:receipt-text-outline',   label: 'Billing' },
    { key: 'mdi:wifi',                   label: 'Internet' },
    { key: 'mdi:paw-outline',            label: 'Pets' },
    { key: 'mdi:message-alert-outline',  label: 'Other' },
];

const colorFor = (key) => COLOR_PALETTE.find(c => c.key === key) || COLOR_PALETTE[6];
const iconFor = (key) => key || 'mdi:tag-outline';

const emptyForm = { name: '', description: '', icon: 'mdi:message-alert-outline', color: 'slate', default_assignee_user_id: '' };

const fullName = (u) => {
    if (!u) return '';
    const full = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
    return full || u.email || `User #${u.user_id}`;
};

const ComplaintCategories = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();
    const {
        categoriesLoading,
        categoriesData,
        categoryMutationLoading,
    } = useSelector(state => state.ComplaintReducer || {});
    const { usernamesDropdownData } = useSelector(state => state.EmployeeReducer || {});
    const assignableUsers = useMemo(
        () => (Array.isArray(usernamesDropdownData) ? usernamesDropdownData : []),
        [usernamesDropdownData]
    );

    const [search, setSearch] = useState('');
    const [modal, setModal] = useState(null); // { mode, category }
    const [form, setForm] = useState(emptyForm);
    const [errors, setErrors] = useState({});
    const [deleteTarget, setDeleteTarget] = useState(null);
    // In-memory only — user picks a preferred view and moves on.
    const [viewMode, setViewMode] = useState('card');

    const fetchList = useCallback(() => {
        if (!societyId) return;
        dispatch(getComplaintCategories({
            societyId,
            search: search || undefined,
            includeInactive: false,
        }));
    }, [dispatch, societyId, search]);
    usePageRefresh(fetchList);

    useEffect(() => { fetchList(); }, [fetchList]);

    const rows = useMemo(
        () => (Array.isArray(categoriesData) ? categoriesData : []),
        [categoriesData]
    );

    const totalUsage = useMemo(
        () => rows.reduce((n, c) => n + Number(c.complaints_count || 0), 0),
        [rows]
    );

    // Lazy-load the assignable users list once the modal opens — matches the
    // "employees only" spec (dropdown pulls active society members, so the
    // default assignee dropdown surfaces exactly the eligible pool).
    const loadAssignees = () => {
        if (societyId) dispatch(getUsernamesDropdown({ societyId }));
    };

    const openCreate = () => {
        setForm(emptyForm);
        setErrors({});
        setModal({ mode: 'create' });
        loadAssignees();
    };
    
    const openEdit = (cat) => {
        setForm({
            name: cat.name || '',
            description: cat.description || '',
            icon: cat.icon || 'mdi:message-alert-outline',
            color: cat.color || 'slate',
            default_assignee_user_id: cat.default_assignee_user_id ? String(cat.default_assignee_user_id) : '',
        });
        setErrors({});
        setModal({ mode: 'edit', category: cat });
        loadAssignees();
    };
    const closeModal = () => {
        setModal(null);
        setForm(emptyForm);
        setErrors({});
    };

    const validate = () => {
        const e = {};
        if (!form.name.trim()) e.name = 'Name is required';
        else if (form.name.trim().length < 2) e.name = 'Minimum 2 characters';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSave = () => {
        if (!validate()) return;
        const payload = {
            name: form.name.trim(),
            description: form.description.trim() || undefined,
            icon: form.icon || undefined,
            color: form.color || undefined,
            // Send `null` explicitly on edit to clear a previously-set assignee;
            // create form uses `undefined` so the backend accepts the omitted key.
            default_assignee_user_id: form.default_assignee_user_id
                ? Number(form.default_assignee_user_id)
                : (modal?.mode === 'edit' ? null : undefined),
        };
        if (modal.mode === 'create') {
            dispatch(createComplaintCategory({
                society_id: societyId,
                ...payload,
                onSuccess: () => { closeModal(); fetchList(); },
            }));
        } else {
            dispatch(updateComplaintCategory({
                id: modal.category.category_id,
                ...payload,
                onSuccess: () => { closeModal(); fetchList(); },
            }));
        }
    };

    const handleDelete = () => {
        if (!deleteTarget) return;
        dispatch(deleteComplaintCategory({
            id: deleteTarget.category_id,
            onSuccess: () => { setDeleteTarget(null); fetchList(); },
        }));
    };

    const activeCat = modal?.mode === 'edit' ? modal.category : null;
    const currentColor = colorFor(form.color);

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen">
            <div className="tw-max-w-6xl tw-mx-auto tw-space-y-4">
                <button
                    onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_COMPLAINTS)}
                    className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-600 hover:tw-text-gray-800"
                >
                    <Icon icon="mdi:arrow-left" /> Back to Complaints
                </button>

                {/* Header + stats */}
                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-4">
                    <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                        <div>
                            <h1 className="tw-text-base tw-font-bold tw-text-gray-800">Complaint Categories</h1>
                            <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">Society-scoped categories with an icon and colour for visual grouping</p>
                        </div>
                        <div className="tw-flex tw-items-center tw-gap-2">
                            {/* Card / List view toggle — segmented control */}
                            <div className="tw-inline-flex tw-p-0.5 tw-bg-gray-100 tw-rounded-md">
                                <button
                                    type="button"
                                    onClick={() => setViewMode('card')}
                                    title="Card view"
                                    className={`tw-flex tw-items-center tw-gap-1 tw-px-2.5 tw-py-1.5 tw-rounded tw-text-xs tw-font-medium tw-transition-colors ${viewMode === 'card' ? 'tw-bg-white tw-text-gray-800 tw-shadow-sm' : 'tw-text-gray-500 hover:tw-text-gray-700'}`}
                                >
                                    <Icon icon="mdi:view-grid-outline" className="tw-text-base" />
                                    Cards
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setViewMode('list')}
                                    title="List view"
                                    className={`tw-flex tw-items-center tw-gap-1 tw-px-2.5 tw-py-1.5 tw-rounded tw-text-xs tw-font-medium tw-transition-colors ${viewMode === 'list' ? 'tw-bg-white tw-text-gray-800 tw-shadow-sm' : 'tw-text-gray-500 hover:tw-text-gray-700'}`}
                                >
                                    <Icon icon="mdi:format-list-bulleted" className="tw-text-base" />
                                    List
                                </button>
                            </div>
                            <button
                                onClick={openCreate}
                                className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700 tw-shadow-sm"
                            >
                                <Icon icon="mdi:plus" className="tw-text-base" />
                                New Category
                            </button>
                        </div>
                    </div>

                    <div className="tw-mt-4 tw-flex tw-flex-wrap tw-gap-2">
                        <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-1 tw-text-[11px] tw-font-semibold tw-rounded-full tw-bg-gray-100 tw-text-gray-700">
                            <Icon icon="mdi:tag-multiple-outline" /> {rows.length} categor{rows.length === 1 ? 'y' : 'ies'}
                        </span>
                        <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-1 tw-text-[11px] tw-font-semibold tw-rounded-full tw-bg-blue-50 tw-text-blue-700">
                            <Icon icon="mdi:message-badge-outline" /> {totalUsage} complaints filed
                        </span>
                    </div>
                </div>

                {/* Search */}
                <div className="tw-relative">
                    <Icon icon="mdi:magnify" className="tw-absolute tw-left-3 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400" />
                    <input
                        type="text"
                        placeholder="Filter categories..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="tw-w-full tw-max-w-md tw-pl-9 tw-pr-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500 tw-bg-white"
                    />
                </div>

                {/* Grid */}
                {categoriesLoading ? (
                    <div className="tw-flex tw-justify-center tw-py-16">
                        <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-2 tw-border-gray-200 tw-border-t-rose-500" />
                    </div>
                ) : rows.length === 0 ? (
                    <div className="tw-bg-white tw-rounded-xl tw-border tw-border-dashed tw-border-gray-200 tw-p-10 tw-text-center">
                        <div className="tw-mx-auto tw-w-12 tw-h-12 tw-rounded-full tw-bg-rose-50 tw-text-rose-500 tw-flex tw-items-center tw-justify-center">
                            <Icon icon="mdi:tag-plus-outline" className="tw-text-2xl" />
                        </div>
                        <h3 className="tw-mt-3 tw-text-sm tw-font-bold tw-text-gray-800">No categories yet</h3>
                        <p className="tw-mt-1 tw-text-[11px] tw-text-gray-500">Categories are required before residents can raise complaints. Create your first one to unlock complaint intake.</p>
                        <button
                            onClick={openCreate}
                            className="tw-mt-4 tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700"
                        >
                            <Icon icon="mdi:plus" /> Create Category
                        </button>
                    </div>
                ) : viewMode === 'card' ? (
                    <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-3">
                        {rows.map(cat => {
                            const color = colorFor(cat.color);
                            const count = Number(cat.complaints_count || 0);
                            return (
                                <div
                                    key={cat.category_id}
                                    className="tw-group tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm hover:tw-shadow-md tw-transition-all tw-p-4 tw-relative"
                                >
                                    <div className="tw-flex tw-items-start tw-gap-3">
                                        <div className={`tw-w-11 tw-h-11 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 ${color.bg} ${color.text}`}>
                                            <Icon icon={iconFor(cat.icon)} className="tw-text-xl" />
                                        </div>
                                        <div className="tw-min-w-0 tw-flex-1">
                                            <h3 className="tw-text-sm tw-font-bold tw-text-gray-800 tw-truncate">{cat.name}</h3>
                                            <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5 tw-line-clamp-2">
                                                {cat.description || 'No description'}
                                            </p>
                                            {cat.defaultAssignee && (
                                                <p className="tw-mt-2 tw-inline-flex tw-items-center tw-gap-1 tw-text-[10px] tw-text-gray-600 tw-bg-gray-50 tw-px-1.5 tw-py-0.5 tw-rounded">
                                                    <Icon icon="mdi:account-arrow-right-outline" /> Assigns to {fullName(cat.defaultAssignee)}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="tw-mt-3 tw-flex tw-items-center tw-justify-between tw-pt-3 tw-border-t tw-border-gray-100">
                                        <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-rounded-full ${color.bg} ${color.text}`}>
                                            <Icon icon="mdi:message-badge-outline" /> {count} complaint{count === 1 ? '' : 's'}
                                        </span>
                                        <div className="tw-inline-flex tw-items-center tw-gap-1">
                                            <button
                                                title="Edit"
                                                onClick={() => openEdit(cat)}
                                                className="tw-p-1.5 tw-rounded hover:tw-bg-amber-50 tw-text-amber-600"
                                            >
                                                <Icon icon="mdi:pencil-outline" className="tw-text-base" />
                                            </button>
                                            <button
                                                title={count > 0 ? `In use by ${count} complaint(s) — deleting will hide it from new complaint forms` : 'Delete'}
                                                onClick={() => setDeleteTarget(cat)}
                                                className="tw-p-1.5 tw-rounded hover:tw-bg-rose-50 tw-text-rose-600"
                                            >
                                                <Icon icon="mdi:trash-can-outline" className="tw-text-base" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    /* List / table view — dense, scannable version of the same rows. */
                    <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-overflow-hidden">
                        <div className="tw-overflow-x-auto">
                            <table className="tw-w-full tw-text-xs">
                                <thead>
                                    <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-100 tw-text-gray-500">
                                        <th className="tw-text-left tw-px-4 tw-py-2.5 tw-font-semibold">Category</th>
                                        <th className="tw-text-left tw-px-4 tw-py-2.5 tw-font-semibold">Default Assignee</th>
                                        <th className="tw-text-right tw-px-4 tw-py-2.5 tw-font-semibold">Complaints</th>
                                        <th className="tw-text-right tw-px-4 tw-py-2.5 tw-font-semibold">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map(cat => {
                                        const color = colorFor(cat.color);
                                        const count = Number(cat.complaints_count || 0);
                                        return (
                                            <tr key={cat.category_id} className="tw-border-b tw-border-gray-50 hover:tw-bg-gray-50/40">
                                                <td className="tw-px-4 tw-py-2.5">
                                                    <div className="tw-flex tw-items-center tw-gap-2">
                                                        <div className={`tw-w-8 tw-h-8 tw-rounded-md tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 ${color.bg} ${color.text}`}>
                                                            <Icon icon={iconFor(cat.icon)} className="tw-text-sm" />
                                                        </div>
                                                        <div className="tw-min-w-0">
                                                            <p className="tw-font-semibold tw-text-gray-800 tw-truncate">{cat.name}</p>
                                                            {cat.description && (
                                                                <p className="tw-text-[10px] tw-text-gray-500 tw-truncate">{cat.description}</p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="tw-px-4 tw-py-2.5 tw-text-gray-700">
                                                    {cat.defaultAssignee ? fullName(cat.defaultAssignee) : <span className="tw-text-gray-400">Unassigned</span>}
                                                </td>
                                                <td className="tw-px-4 tw-py-2.5 tw-text-right">
                                                    <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-rounded-full ${color.bg} ${color.text}`}>
                                                        {count}
                                                    </span>
                                                </td>
                                                <td className="tw-px-4 tw-py-2.5 tw-text-right">
                                                    <div className="tw-inline-flex tw-items-center tw-gap-1">
                                                        <button
                                                            title="Edit"
                                                            onClick={() => openEdit(cat)}
                                                            className="tw-p-1.5 tw-rounded hover:tw-bg-amber-50 tw-text-amber-600"
                                                        >
                                                            <Icon icon="mdi:pencil-outline" className="tw-text-base" />
                                                        </button>
                                                        <button
                                                            title={count > 0 ? `In use by ${count} complaint(s) — deleting will hide it from new complaint forms` : 'Delete'}
                                                            onClick={() => setDeleteTarget(cat)}
                                                            className="tw-p-1.5 tw-rounded hover:tw-bg-rose-50 tw-text-rose-600"
                                                        >
                                                            <Icon icon="mdi:trash-can-outline" className="tw-text-base" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {/* Create / Edit modal */}
            {modal && (
                <div className="tw-fixed tw-inset-0 tw-bg-black/40 tw-flex tw-items-center tw-justify-center tw-z-50 tw-p-4">
                    <div className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-lg tw-max-h-[90vh] tw-flex tw-flex-col tw-overflow-hidden">
                        <div className="tw-p-4 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-justify-between">
                            <div className="tw-flex tw-items-center tw-gap-3">
                                <div className={`tw-w-9 tw-h-9 tw-rounded-lg tw-flex tw-items-center tw-justify-center ${currentColor.bg} ${currentColor.text}`}>
                                    <Icon icon={iconFor(form.icon)} className="tw-text-lg" />
                                </div>
                                <div>
                                    <h3 className="tw-text-sm tw-font-bold tw-text-gray-800">
                                        {modal.mode === 'create' ? 'New Category' : 'Edit Category'}
                                    </h3>
                                    <p className="tw-text-[11px] tw-text-gray-500">
                                        {activeCat?.complaints_count > 0
                                            ? `Currently used by ${activeCat.complaints_count} complaint(s)`
                                            : 'Pick an icon and color to help staff spot it quickly'}
                                    </p>
                                </div>
                            </div>
                            <button onClick={closeModal} className="tw-p-1 tw-rounded hover:tw-bg-gray-100 tw-text-gray-500">
                                <Icon icon="mdi:close" className="tw-text-lg" />
                            </button>
                        </div>

                        <div className="tw-p-5 tw-space-y-4 tw-overflow-y-auto">
                            {/* Name */}
                            <div>
                                <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-600 tw-mb-1">
                                    Name <span className="tw-text-rose-500">*</span>
                                </label>
                                <input
                                    value={form.name}
                                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                                    className="tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500"
                                    placeholder="e.g. Plumbing"
                                />
                                {errors.name && <p className="tw-text-[10px] tw-text-rose-600 tw-mt-1">{errors.name}</p>}
                            </div>

                            {/* Description */}
                            <div>
                                <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-600 tw-mb-1">Description</label>
                                <textarea
                                    rows={2}
                                    value={form.description}
                                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                                    className="tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500"
                                    placeholder="Optional — what this category covers"
                                />
                            </div>

                            {/* Default assignee */}
                            <div>
                                <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-600 tw-mb-1">
                                    Default Assignee
                                </label>
                                <select
                                    value={form.default_assignee_user_id}
                                    onChange={e => setForm(f => ({ ...f, default_assignee_user_id: e.target.value }))}
                                    className="tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500"
                                >
                                    <option value="">Unassigned (route manually)</option>
                                    {assignableUsers.map(u => (
                                        <option key={u.user_id} value={u.user_id}>{fullName(u)}</option>
                                    ))}
                                </select>
                                <p className="tw-text-[10px] tw-text-gray-500 tw-mt-1">
                                    Every complaint raised under this category will auto-assign to this person.
                                </p>
                            </div>

                            {/* Colour picker */}
                            <div>
                                <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-600 tw-mb-2">Color</label>
                                <div className="tw-flex tw-flex-wrap tw-gap-2">
                                    {COLOR_PALETTE.map(c => (
                                        <button
                                            key={c.key}
                                            type="button"
                                            onClick={() => setForm(f => ({ ...f, color: c.key }))}
                                            title={c.key}
                                            className={`tw-w-8 tw-h-8 tw-rounded-full ${c.swatch} tw-transition-all ${form.color === c.key ? `tw-ring-2 tw-ring-offset-2 ${c.ring}` : 'hover:tw-scale-110'}`}
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* Icon picker */}
                            <div>
                                <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-600 tw-mb-2">Icon</label>
                                <div className="tw-grid tw-grid-cols-8 tw-gap-2">
                                    {ICON_LIBRARY.map(i => (
                                        <button
                                            key={i.key}
                                            type="button"
                                            onClick={() => setForm(f => ({ ...f, icon: i.key }))}
                                            title={i.label}
                                            className={`tw-aspect-square tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-transition-all ${form.icon === i.key ? `${currentColor.bg} ${currentColor.text} tw-border-transparent tw-ring-2 ${currentColor.ring}` : 'tw-border-gray-200 tw-text-gray-500 hover:tw-bg-gray-50'}`}
                                        >
                                            <Icon icon={i.key} className="tw-text-lg" />
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Preview */}
                            <div className="tw-pt-2 tw-border-t tw-border-gray-100">
                                <p className="tw-text-[10px] tw-uppercase tw-tracking-wide tw-text-gray-400 tw-font-semibold tw-mb-2">Preview</p>
                                <div className="tw-inline-flex tw-items-center tw-gap-2 tw-px-3 tw-py-1.5 tw-rounded-lg tw-border tw-border-gray-100">
                                    <div className={`tw-w-8 tw-h-8 tw-rounded-lg tw-flex tw-items-center tw-justify-center ${currentColor.bg} ${currentColor.text}`}>
                                        <Icon icon={iconFor(form.icon)} />
                                    </div>
                                    <div>
                                        <p className="tw-text-xs tw-font-semibold tw-text-gray-800">{form.name || 'Category name'}</p>
                                        <p className="tw-text-[10px] tw-text-gray-500">{form.description || 'No description'}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="tw-p-4 tw-border-t tw-border-gray-100 tw-flex tw-justify-end tw-gap-2 tw-bg-gray-50/50">
                            <button onClick={closeModal} className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50 tw-bg-white">Cancel</button>
                            <button
                                onClick={handleSave}
                                disabled={categoryMutationLoading}
                                className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700 tw-rounded-md disabled:tw-opacity-50"
                            >
                                {categoryMutationLoading && <div className="tw-animate-spin tw-rounded-full tw-h-3 tw-w-3 tw-border-2 tw-border-white tw-border-t-transparent" />}
                                {modal.mode === 'create' ? 'Create' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete confirm */}
            {deleteTarget && (
                <div className="tw-fixed tw-inset-0 tw-bg-black/40 tw-flex tw-items-center tw-justify-center tw-z-50 tw-p-4">
                    <div className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-sm tw-overflow-hidden">
                        <div className="tw-p-4 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-gap-3">
                            <div className="tw-w-9 tw-h-9 tw-rounded-lg tw-bg-rose-50 tw-text-rose-600 tw-flex tw-items-center tw-justify-center">
                                <Icon icon="mdi:alert-outline" className="tw-text-lg" />
                            </div>
                            <div>
                                <h3 className="tw-text-sm tw-font-bold tw-text-gray-800">Delete Category?</h3>
                                <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">Existing complaints keep the category; it just won't appear in new complaint forms.</p>
                            </div>
                        </div>
                        <div className="tw-p-5">
                            {Number(deleteTarget.complaints_count) > 0 && (
                                <div className="tw-mb-3 tw-p-2 tw-bg-amber-50 tw-border tw-border-amber-100 tw-rounded-md">
                                    <p className="tw-text-[11px] tw-text-amber-800">
                                        <Icon icon="mdi:information-outline" className="tw-inline tw-mr-1" />
                                        This category is used by <strong>{deleteTarget.complaints_count}</strong> complaint(s).
                                    </p>
                                </div>
                            )}
                            <p className="tw-text-xs tw-text-gray-600 tw-mb-4">
                                Delete <span className="tw-font-semibold">{deleteTarget.name}</span>?
                            </p>
                            <div className="tw-flex tw-justify-end tw-gap-2">
                                <button onClick={() => setDeleteTarget(null)} className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50">Cancel</button>
                                <button
                                    onClick={handleDelete}
                                    disabled={categoryMutationLoading}
                                    className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700 tw-rounded-md disabled:tw-opacity-50"
                                >
                                    {categoryMutationLoading ? 'Deleting…' : 'Delete'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ComplaintCategories;
